import { formatMonthNumericValueToString } from '../../../../utils/DateUtils';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { errorAlert } from '../../alert/defaultAlert';
import InputText from '../../Forms/Element/InputText';
import { formatCardNumber, formatCardExpiry, parseCardExpiry, isCardExpiryValid } from '../../../pages/charge/utils/ChargeUtils';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import { ApiError } from '../../Common/ApiError';
import CardBrands, { detectCardBrand } from './CardBrands';
import './ChargeCard.css';

// URL oficial da biblioteca de tokenizacao do cel.cash (Celcoin/Galax Pay).
// Ela criptografa o cartao no navegador e devolve uma hash - o numero e o cvv
// nunca chegam ao nosso backend.
const CELCOIN_CHECKOUT_JS = 'https://js.cel.cash/checkout.min.js';

// Carrega o checkout.min.js uma unica vez e resolve quando window.GalaxPay existe.
let celcoinScriptPromise = null;
const loadCelcoinScript = () => {
    if (window.GalaxPay) {
        return Promise.resolve();
    }

    if (celcoinScriptPromise) {
        return celcoinScriptPromise;
    }

    celcoinScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = CELCOIN_CHECKOUT_JS;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            celcoinScriptPromise = null;
            reject(new Error('Não foi possível carregar a tokenização do cel.cash.'));
        };
        document.body.appendChild(script);
    });

    return celcoinScriptPromise;
};

/**
 * Coleta o cartao e o tokeniza no navegador via cel.cash, entregando ao form
 * apenas a hash (setPaymentToken), seguindo o padrão do formulário de cartão
 * do provedor. O cel.cash exige o nome do titular, entao
 * ha um campo extra "Nome no cartao".
 */
// Pausa após a última tecla antes de tokenizar. Curto o bastante para o token estar
// pronto quando o operador alcança o botão salvar.
const TOKENIZATION_DEBOUNCE_MS = 350;

const CelcoinCard = (props) => {

    const { t } = useTranslation();
    // setTokenizing avisa o formulário que há uma hash em andamento, para o salvar não
    // partir com o token ainda nulo.
    const { setPaymentToken, reset, setTokenizing } = props;

    const [cardNumber, setCardNumber] = useState('');
    const [cardHolder, setCardHolder] = useState('');
    const [cardCvv, setCardCvv] = useState('');
    // Validade digitada em texto livre no formato "MM/AA" (impresso no próprio
    // cartão) em vez de dois selects separados de mês e ano.
    const [cardExpiry, setCardExpiry] = useState('');
    const [cardType, setCardType] = useState(null);
    const [cardError, setCardError] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const [tokenizerReady, setTokenizerReady] = useState(false);

    // Instancia da lib do cel.cash, criada assim que o publicToken chega. A
    // criacao ja dispara o download da chave publica usada na criptografia.
    const galaxPayRef = useRef(null);
    // Identifica a tokenização mais recente. A lib responde por callback, e sem isso a
    // resposta de uma digitação antiga pode chegar depois da nova e sobrescrever a hash
    // válida por uma de um cartão que o operador já corrigiu.
    const hashRequestRef = useRef(0);

    // Busca o publicToken + ambiente (sandbox) e inicializa a lib uma vez.
    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            try {
                const resp = await axiosInstance.get(Api.parameter.celcoin_public_config);
                const publicToken = resp?.data?.publicToken;
                const sandbox = Boolean(resp?.data?.sandbox);

                if (!publicToken) {
                    setCardError(t('validation.custom.celcoin.public_token_missing'));
                    return;
                }

                await loadCelcoinScript();

                if (cancelled) {
                    return;
                }

                // O segundo parametro e "environment": true = producao, false = sandbox.
                galaxPayRef.current = new window.GalaxPay(publicToken, !sandbox);
                setTokenizerReady(true);
            } catch (error) {
                if (!cancelled) {
                    setTokenizerReady(false);
                    setCardError(t('validation.custom.celcoin.tokenization_failed'));
                    ApiError(error);
                }
            }
        };

        init();

        return () => {
            cancelled = true;
        };
    }, [t]);

    // Gera a hash do cartao no cel.cash e devolve ao form.
    const generateHash = () => {
        if (!galaxPayRef.current) {
            // Sem SDK não haverá hash nenhuma: liberar o salvar aqui é obrigatório.
            // O useEffect abaixo trava o formulário assim que o cartão fica completo,
            // e sair deste return sem destravar deixava o operador com a mensagem de
            // erro na tela e o botão Salvar morto até recarregar a página.
            setShowLoading(false);
            setTokenizing?.(false);
            setPaymentToken(null);
            setCardError(t('validation.custom.celcoin.public_token_missing'));
            return;
        }

        const requestId = hashRequestRef.current + 1;
        hashRequestRef.current = requestId;

        setShowLoading(true);
        setTokenizing?.(true);
        setCardError(null);

        try {
            // Validade no formato yyyy-MM exigido pelo cel.cash.
            const expiry = parseCardExpiry(cardExpiry);
            const expiresAt = `${expiry.year}-${formatMonthNumericValueToString(expiry.month)}`;
            const card = galaxPayRef.current.newCard({
                number: cardNumber.replace(/\D/g, ''),
                holder: cardHolder,
                expiresAt,
                cvv: cardCvv
            });

            galaxPayRef.current.hashCreditCard(
                card,
                (hash) => {
                    // Descarta a resposta de uma digitação que já foi superada.
                    if (hashRequestRef.current !== requestId) {
                        return;
                    }
                    setPaymentToken(hash);
                    setShowLoading(false);
                    setTokenizing?.(false);
                },
                (error) => {
                    if (hashRequestRef.current !== requestId) {
                        return;
                    }
                    setShowLoading(false);
                    setTokenizing?.(false);
                    setCardError(error?.message || t('validation.custom.celcoin.tokenization_failed'));
                    errorAlert(error?.message || t('validation.custom.celcoin.tokenization_failed'));
                }
            );
        } catch (error) {
            // newCard lanca ('Número do cartão inválido.', 'CVV inválido.' etc.)
            setShowLoading(false);
            setTokenizing?.(false);
            setCardError(error?.message || t('validation.custom.celcoin.tokenization_failed'));
        }
    };

    // Quando todos os campos estao preenchidos, tokeniza. Reprocessa a hash a
    // cada alteracao (a hash e temporaria - expira em ~5 min no cel.cash).
    //
    // A chamada espera o operador parar de digitar. Sem essa pausa, cada tecla no CVV ou
    // no nome disparava uma tokenização, e quem digita rápido produzia várias chamadas
    // concorrentes para o mesmo cartão.
    useEffect(() => {
        const cvvComplete = cardCvv && cardCvv.length >= 3;
        const expiry = parseCardExpiry(cardExpiry);
        const expiryComplete = expiry !== null && isCardExpiryValid(expiry);
        // Menos de 13 dígitos não é cartão nenhum: evita o cel.cash recusar um número
        // que o operador ainda está digitando.
        const numberComplete = cardNumber.replace(/\D/g, '').length >= 13;

        if (!(numberComplete && cardHolder && expiryComplete && cvvComplete)) {
            // Enquanto o cartao nao esta completo, garante que nao haja hash antiga.
            hashRequestRef.current += 1;
            setPaymentToken(null);
            setTokenizing?.(false);
            return undefined;
        }

        // O formulário já fica travado aqui, e não só quando a chamada sai: entre a última
        // tecla e o disparo há uma janela em que o cartão está completo e o token ainda é o
        // antigo (ou nulo). Salvar nessa janela era o que produzia "Informe os dados do
        // cartão para gerar a hash".
        setTokenizing?.(true);

        const timer = setTimeout(generateHash, TOKENIZATION_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardNumber, cardHolder, cardExpiry, cardCvv, tokenizerReady]);

    // Valida a validade assim que os 4 dígitos (MM/AA) são digitados, sem
    // esperar o usuário preencher número/CVV para dar o retorno de erro.
    useEffect(() => {
        const digitsOnly = String(cardExpiry || '').replace(/\D/g, '');
        if (digitsOnly.length < 4) {
            return;
        }

        const expiry = parseCardExpiry(cardExpiry);
        if (expiry.month < 1 || expiry.month > 12) {
            setCardError(t('validation.invalid.card_expiry'));
        } else if (!isCardExpiryValid(expiry)) {
            setCardError(t('validation.invalid.card_expiry_expired'));
        }
    }, [cardExpiry, t]);

    useEffect(() => {
        if (reset) {
            setPaymentToken(null);
            setCardNumber('');
            setCardHolder('');
            setCardCvv('');
            setCardExpiry('');
            setCardType(null);
            setCardError(null);
        }
    }, [reset, setPaymentToken]);

    return (
        <div className="charge-card__wrapper">
            {showLoading && (
                <div className="charge-card__token-overlay">
                    <div className="charge-card__token-overlay-inner">
                        <div className="charge-card__token-spinner">
                            <i className="fa-solid fa-spinner-third fa-spin"></i>
                        </div>
                        <span className="charge-card__token-label">
                            <i className="fa-solid fa-lock me-2"></i>
                            {t('message.generating_payment_token')}
                        </span>
                    </div>
                </div>
            )}
            <div className="row">
                <div className="col-xl-12">
                    <div className="mb-3">
                        {cardError && (
                            <div className="alert alert-danger" role="alert">
                                {cardError}
                            </div>
                        )}

                        <CardBrands brand={cardType} className="card-brands--celcoin-form" />

                        <InputText
                            placeholder={t('payment_card_number')}
                            // Formata e identifica a bandeira já na digitação: o operador vê o
                            // cartão ser reconhecido sem precisar sair do campo.
                            onChange={(e) => {
                                const value = formatCardNumber(e.target.value);
                                setCardNumber(value);
                                setCardType(detectCardBrand(value));
                            }}
                            value={cardNumber}
                            maxLength={19}
                        />
                    </div>
                </div>
                <div className="col-xl-12">
                    <div className="mb-3">
                        <InputText
                            placeholder={t('payment_card_holder')}
                            value={cardHolder}
                            onChange={(e) => setCardHolder(e.target.value)}
                        />
                    </div>
                </div>
                <div className="col-xl-6">
                    <div className="mb-3">
                        <InputText
                            placeholder={t('payment_card_expiry')}
                            value={cardExpiry}
                            onChange={(e) => setCardExpiry(formatCardExpiry(e.target.value))}
                            maxLength={5}
                        />
                    </div>
                </div>
                <div className="col-xl-6">
                    <div className="input-group mb-3">
                        <InputText
                            placeholder={t('payment_card_cvv')}
                            value={cardCvv}
                            onChange={(e) => setCardCvv(e.target.value)}
                            maxLength={4} />
                        <span className="input-group-text" id="basic-addon2"><i className="fa-solid fa-credit-card"></i></span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CelcoinCard;
